import * as React from 'react'
import type { ReactElement } from 'react'
import { Check, Moon, Sun, SunMoon } from 'lucide-react'
import { useTheme } from '@/theme/ThemeProvider'
import { THEME_PREFERENCES, type ThemePreference } from '@/helpers/theme'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

// "Match this device" rather than "System", and a sun-and-moon rather than a
// laptop: the laptop said nothing about light or dark to anyone reading it
// literally.
const THEME_LABELS: Record<ThemePreference, string> = {
  system: 'Match this device',
  light: 'Light',
  dark: 'Dark',
}

const THEME_ICONS: Record<ThemePreference, ReactElement> = {
  system: <SunMoon className="h-4 w-4" />,
  light: <Sun className="h-4 w-4" />,
  dark: <Moon className="h-4 w-4" />,
}

const OPTIONS = THEME_PREFERENCES.flatMap((value) => {
  const label = THEME_LABELS[value]
  const icon = THEME_ICONS[value]
  return label === undefined || icon === undefined ? [] : [{ value, label, icon }]
})

/**
 * Quiet list of System / Light / Dark — same pattern as Notion appearance.
 * Use inline (profile) or inside a popover (sidebar trigger).
 */
export function AppearanceOptions({
  className,
  onSelect,
}: {
  className?: string
  /** Called after a choice (e.g. close a popover). */
  onSelect?: () => void
}): ReactElement {
  const { preference, setPreference } = useTheme()

  return (
    <div className={cn('flex flex-col gap-0.5', className)} role="listbox" aria-label="Appearance">
      {OPTIONS.map((opt) => {
        const selected = preference === opt.value
        return (
          <button
            key={opt.value}
            type="button"
            role="option"
            aria-selected={selected}
            onClick={() => {
              setPreference(opt.value)
              onSelect?.()
            }}
            className={cn(
              'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm outline-none transition-colors focus-visible:focus-ds',
              selected
                ? 'bg-surface-current font-medium text-ink'
                : 'text-ink-muted hover:bg-surface-hover hover:text-ink',
            )}
          >
            <span className="text-ink-muted">{opt.icon}</span>
            <span className="flex-1">{opt.label}</span>
            {selected ? <Check className="h-3.5 w-3.5 text-ink" aria-hidden /> : null}
          </button>
        )
      })}
    </div>
  )
}

function TriggerIcon(): ReactElement {
  const { preference, resolved } = useTheme()
  if (preference === 'system') return <SunMoon className="h-4 w-4" />
  if (resolved === 'dark') return <Moon className="h-4 w-4" />
  return <Sun className="h-4 w-4" />
}

/**
 * Compact sidebar / chrome control: icon opens a minimal appearance menu.
 */
export function ThemeMenuButton({ className }: { className?: string }): ReactElement {
  const [open, setOpen] = React.useState(false)
  // The icon alone is the current choice; the words say what the button is for.
  const { preference } = useTheme()
  const label = `Appearance: ${THEME_LABELS[preference] ?? ''}`

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip content={label}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={label}
            className={cn(
              // It lives in the sidebar, which is blackwood in both themes.
              'rounded-sm p-1.5 text-sidebar-ink-muted outline-none transition-colors hover:bg-sidebar-hover hover:text-sidebar-ink focus-visible:focus-ds',
              className,
            )}
          >
            <TriggerIcon />
          </button>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent align="end" side="top" className="w-44 p-1.5">
        <p className="px-2 py-1 text-xs text-ink-muted">Appearance</p>
        <AppearanceOptions onSelect={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  )
}
